import type { MeOutput } from '@garage/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { api } from '../../shared/api.js';
import { clearAppBadge } from '../../shared/app-badge.js';
import { ME_QUERY_KEY } from '../../shared/auth.js';
import { clearLastAccess } from '../../shared/last-access.js';
import { clearSessionIssue } from '../../shared/session-issues.js';
import { landingPath } from './paths.js';

/** Oublie toutes les données en cache sauf `me` (changement de garage, de session ou de mode support). */
export function useResetGarageCache() {
  const qc = useQueryClient();
  return useCallback(
    (me: MeOutput | null) => {
      // Les données de l'ancien garage ne doivent jamais rester en cache.
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== ME_QUERY_KEY[0] });
      qc.setQueryData(ME_QUERY_KEY, me);
      clearSessionIssue();
    },
    [qc],
  );
}

/**
 * Après connexion, invitation acceptée, entrée ou sortie du mode support : mémorise `me` et oriente
 * (`landingPath`). `afterLogin` : l'administrateur plateforme membre d'un garage choisit toujours.
 */
export function useEnterSession() {
  const reset = useResetGarageCache();
  const navigate = useNavigate();
  return useCallback(
    async (me: MeOutput, opts: { afterLogin?: boolean; to?: string } = {}) => {
      reset(me);
      await navigate({ to: opts.to ?? landingPath(me, { afterLogin: opts.afterLogin }) });
    },
    [reset, navigate],
  );
}

/**
 * « Quitter le mode support » (bandeau, menu du compte, Compte) : `platform.support.leave` sort de la
 * session support **sans rendre l'accès** (accordé jusqu'à son échéance ; « Entrer à nouveau » depuis la
 * console), puis retour à `/admin`. « Rendre l'accès » reste distinct (`platform.support.end`).
 */
export function useLeaveSupport() {
  const enter = useEnterSession();
  return useMutation({
    mutationFn: () => api('platform.support.leave'),
    onSuccess: async (me) => {
      clearLastAccess();
      await enter(me, { to: '/admin' });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useCallback(async () => {
    try {
      await api('auth.logout');
    } finally {
      qc.clear();
      qc.setQueryData(ME_QUERY_KEY, null);
      clearSessionIssue();
      clearAppBadge();
      await navigate({ to: '/login' });
    }
  }, [qc, navigate]);
}
